import ExcelJS from "exceljs";
import { t } from "i18next";
import { saveExcelFile, formatDate } from "../../../utils/Utils";
import { capitalize } from "../../../utils/appUtils";
import {
  typeFormatter,
  formatNumericValue,
} from "../../../utils/tableDataFormatter";
import { createCleaningEffectivenessCell } from "../../../utils/cleaningEffectivenessValue";
import {
  BORDER_STYLE,
  HEADER_STYLE,
  SUBHEADER_STYLE,
  DATA_STYLE,
} from "./excelReportStyles";

const ANNUAL_BENEFIT_KEYS = [
  "annual_energy_saving_percent",
  "annual_energy_saving_dollars",
  "annual_chemical_saving_percent",
  "annual_chemical_saving_dollars",
  "annual_downtime_saving_percent",
  "annual_downtime_saving_dollars",
  "annual_downtime_saving_days",
  "annual_increased_production_percent",
  "annual_increased_production_dollars",
  "annual_increased_production",
  "annual_total_financial_benefits",
];

// Formatter for Excel exports - handles null/undefined/empty values
const emptyValueFormatterForExcel = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return value;
};

const formatReason = (reason, proportion) => {
  if (!reason || reason === "-") return "-";

  if (!proportion || proportion <= 0) return reason;

  const propValue = parseFloat(proportion);
  const propFormatted =
    propValue < 10 && propValue % 1 !== 0
      ? `${propValue.toFixed(1)}%`
      : `${Math.round(propValue)}%`;
  return `${reason} (${propFormatted})`;
};

const parseJSON = (data) => {
  if (!data) return {};
  try {
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return {};
  }
};

const formatPlantTrainName = (plantName, plantId, trainNum) =>
  `${plantName || "Plant"}(${plantId}) - Train ${trainNum}`;

const addBordersToSheet = (sheet) => {
  sheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.border = BORDER_STYLE;
    });
  });
};

const createBenchmarkOptimizationSheet = (
  sheet,
  trainsData,
  currencySymbol = "$",
  volumeUnit = "m³",
) => {
  try {
    const hasAnnualBenefits = trainsData.some(
      (train) =>
        train.ca_predictions &&
        ANNUAL_BENEFIT_KEYS.some(
          (key) =>
            train.ca_predictions[key] !== undefined &&
            train.ca_predictions[key] !== null,
        ),
    );

    // Top metrics (Objective and Forecast Window)
    const topMetrics = [
      {
        label: t("benchMark.headers.objective"),
        key: null,
        format: "string",
        isDynamic: true,
        dynamicKey: "optimization_target",
      },
      {
        label: t("benchMark.headers.forecastWindow"),
        key: "forcast_window",
        format: "number",
      },
    ];

    // Benefits metrics (with Total and Annual columns)
    const benefitsMetrics = [
      {
        label: t("benchMark.headers.energySavingsPercent"),
        key: "energy_saving_percent",
        annualKey: "annual_energy_saving_percent",
        format: "percentage",
        unit: "%",
      },
      {
        label: t("benchMark.headers.energySavings"),
        key: "energy_saving_dollars",
        annualKey: "annual_energy_saving_dollars",
        format: "currency",
        unit: currencySymbol,
      },
      {
        label: t("benchMark.headers.chemicalSavingsPercent"),
        key: "chemical_saving_percent",
        annualKey: "annual_chemical_saving_percent",
        format: "percentage",
        unit: "%",
      },
      {
        label: t("benchMark.headers.chemicalSavings"),
        key: "chemical_saving_dollars",
        annualKey: "annual_chemical_saving_dollars",
        format: "currency",
        unit: currencySymbol,
      },
      {
        label: t("benchMark.headers.downtimeSavingsPercent"),
        key: "downtime_saving_percent",
        annualKey: "annual_downtime_saving_percent",
        format: "percentage",
        unit: "%",
      },
      {
        label: t("benchMark.headers.downtimeSavings"),
        key: "downtime_saving_dollars",
        annualKey: "annual_downtime_saving_dollars",
        format: "currency",
        unit: currencySymbol,
      },
      {
        label: t("benchMark.headers.downtimeSavingsDays"),
        key: "downtime_saving_days",
        annualKey: "annual_downtime_saving_days",
        format: "number",
        unit: "days",
      },
      {
        label: t("benchMark.headers.increasedProductionPercent"),
        key: "increased_production_percent",
        annualKey: "annual_increased_production_percent",
        format: "percentage",
        unit: "%",
      },
      {
        label: t("benchMark.headers.energySavings"),
        key: "increased_production_dollars",
        annualKey: "annual_increased_production_dollars",
        format: "currency",
        unit: currencySymbol,
      },
      {
        label: t("benchMark.headers.increasedProduction"),
        key: "increased_production",
        annualKey: "annual_increased_production",
        format: "volume",
        unit: volumeUnit,
      },
      {
        label: t("benchMark.headers.total"),
        key: "total_financial_benefits",
        annualKey: "annual_total_financial_benefits",
        format: "currency",
        unit: currencySymbol,
      },
    ];

    const headerRow1 = sheet.getRow(1);
    const metricCell = headerRow1.getCell(1);
    metricCell.value = t("benchMark.headers.metric");
    Object.assign(metricCell, HEADER_STYLE);

    let colIndex = 2;
    trainsData.forEach((train) => {
      const benefitsCell = headerRow1.getCell(colIndex);
      benefitsCell.value = formatPlantTrainName(
        train.plant_name,
        train.plant_id,
        train.train_num,
      );
      Object.assign(benefitsCell, HEADER_STYLE);
      // Only merge if annual benefits exist
      if (hasAnnualBenefits) {
        sheet.mergeCells(1, colIndex, 1, colIndex + 1);
        colIndex += 2;
      } else {
        colIndex += 1;
      }
    });
    headerRow1.commit();

    // Add header row 2: Sub-headers (only if annual benefits exist)
    if (hasAnnualBenefits) {
      const headerRow2 = sheet.getRow(2);
      headerRow2.getCell(1).value = "";

      colIndex = 2;
      trainsData.forEach(() => {
        const totalCell = headerRow2.getCell(colIndex);
        totalCell.value = t("benchMark.headers.financialBenefits");
        Object.assign(totalCell, SUBHEADER_STYLE);
        colIndex++;

        const annualCell = headerRow2.getCell(colIndex);
        annualCell.value = t("benchMark.headers.annualBenefits");
        Object.assign(annualCell, SUBHEADER_STYLE);
        colIndex++;
      });
      headerRow2.commit();
    }

    // Add top metrics (Objective and Forecast Window) - single column per train
    topMetrics.forEach((metric, metricIndex) => {
      const rowNum = (hasAnnualBenefits ? 3 : 2) + metricIndex;
      const row = sheet.getRow(rowNum);

      const labelCell = row.getCell(1);
      labelCell.value = metric.label;
      Object.assign(labelCell, DATA_STYLE);

      const getValue = (train, metric) => {
        if (metric.isDynamic && metric.dynamicKey) {
          return train[metric.dynamicKey] || "-";
        }
        if (metric.value) return metric.value;
        if (train.ca_predictions?.[metric.key]) {
          const value = train.ca_predictions[metric.key];
          return typeof value === "number" ? value.toFixed(2) : value;
        }
        return "-";
      };

      let colIdx = 2;
      trainsData.forEach((train) => {
        const value = getValue(train, metric);
        const dataCell = row.getCell(colIdx++);
        dataCell.value = value;
        Object.assign(dataCell, DATA_STYLE);

        if (hasAnnualBenefits) {
          const annualDataCell = row.getCell(colIdx++);
          annualDataCell.value = value;
          Object.assign(annualDataCell, DATA_STYLE);
        }
      });

      row.commit();
    });

    // Add benefits metrics (with Total and Annual columns if available)
    const formatMetricValue = (value, metric) => {
      if (value == null) return "-";
      const formatted = value.toFixed(2);
      if (metric.format === "currency") return `${metric.unit} ${formatted}`;
      if (metric.format === "volume") return `${formatted} ${metric.unit}`;
      return formatted;
    };

    benefitsMetrics.forEach((metric, metricIndex) => {
      const rowNum = (hasAnnualBenefits ? 5 : 4) + metricIndex;
      const row = sheet.getRow(rowNum);

      const labelCell = row.getCell(1);
      labelCell.value = metric.label;
      Object.assign(labelCell, DATA_STYLE);

      let colIdx = 2;
      trainsData.forEach((train) => {
        const value = train.ca_predictions?.[metric.key];
        const totalCell = row.getCell(colIdx++);
        totalCell.value = formatMetricValue(value, metric);
        Object.assign(totalCell, DATA_STYLE);

        if (hasAnnualBenefits) {
          const annualValue = train.ca_predictions?.[metric.annualKey];
          const annualCell = row.getCell(colIdx++);
          annualCell.value = formatMetricValue(annualValue, metric);
          Object.assign(annualCell, DATA_STYLE);
        }
      });

      row.commit();
    });

    // Set column widths
    sheet.getColumn(1).width = 35;
    let colNum = 2;
    trainsData.forEach((_, index) => {
      sheet.getColumn(colNum).width = 15; // Financial Benefits column
      colNum++;
      if (hasAnnualBenefits) {
        sheet.getColumn(colNum).width = 15; // Annual Benefits column
        colNum++;
      }
    });

    // Set row heights for header
    sheet.getRow(1).height = 20;
    if (hasAnnualBenefits) {
      sheet.getRow(2).height = 20;
    }
    sheet.getRow(3).height = 20;
    sheet.getRow(4).height = 20;

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

/**
 * Creates Benchmark CIP Predict Recommend Sheet
 * Shows predicted vs recommended CIP dates and reasons for each train
 *
 * Data Structure Expected:
 * @param {Object} trainsData - Array of trains with structure:

 */
const createBenchmarkCIPPredictRecommendSheet = (sheet, trainsData) => {
  try {
    if (!Array.isArray(trainsData) || trainsData.length === 0) {
      return;
    }

    const allDates = new Set();
    trainsData.forEach((train) => {
      if (train && train.ca_predictions) {
        if (Array.isArray(train.ca_predictions.recommended_cip_start_date)) {
          train.ca_predictions.recommended_cip_start_date.forEach((date) =>
            allDates.add(date),
          );
        }
        if (Array.isArray(train.ca_predictions.predicted_cip_start_date)) {
          train.ca_predictions.predicted_cip_start_date.forEach((date) =>
            allDates.add(date),
          );
        }
      }
    });

    const sortedDates = Array.from(allDates).sort();

    // Build header structure: Start Date | Train1 (Predict Type, Reason, Recommend Type, Reason) | Train2 (...) | ...
    const headerRow1 = sheet.getRow(1);
    headerRow1.getCell(1).value = t("benchMark.headers.startDate");
    Object.assign(headerRow1.getCell(1), HEADER_STYLE);

    let colIndex = 2;
    const trainColMap = {}; // Map train index to starting column

    trainsData.forEach((train, trainIdx) => {
      trainColMap[trainIdx] = colIndex;

      // Merge cells for train name (4 columns: Predict Type, Reason, Recommend Type, Reason)
      const trainNameCell = headerRow1.getCell(colIndex);
      trainNameCell.value = formatPlantTrainName(
        train.plant_name,
        train.plant_id,
        train.train_num,
      );
      Object.assign(trainNameCell, HEADER_STYLE);

      sheet.mergeCells(1, colIndex, 1, colIndex + 3);
      colIndex += 4;
    });

    // Second header row with sub-columns
    const headerRow2 = sheet.getRow(2);
    headerRow2.getCell(1).value = "";

    colIndex = 2;
    trainsData.forEach((train, trainIdx) => {
      const subHeaders = [
        t("benchMark.headers.predictType"),
        t("benchMark.headers.reason"),
        t("benchMark.headers.recommendType"),
        t("benchMark.headers.recommendReason"),
      ];
      subHeaders.forEach((subHeader) => {
        const cell = headerRow2.getCell(colIndex);
        cell.value = subHeader;
        Object.assign(cell, SUBHEADER_STYLE);
        colIndex++;
      });
    });
    headerRow2.commit();

    // Add data rows
    sortedDates.forEach((date, dateIdx) => {
      const rowNum = 3 + dateIdx;
      const row = sheet.getRow(rowNum);

      // Format date to dd/mm/yyyy
      const dateCell = row.getCell(1);
      dateCell.value = formatDate(date);
      Object.assign(dateCell, DATA_STYLE);

      // Add data for each train
      trainsData.forEach((train, trainIdx) => {
        const startCol = trainColMap[trainIdx];

        if (train.ca_predictions) {
          const ca = train.ca_predictions;

          // Find if this date is predicted or recommended
          const predictedIdx = ca.predicted_cip_start_date
            ? ca.predicted_cip_start_date.findIndex((d) => d === date)
            : -1;

          const recommendedIdx = ca.recommended_cip_start_date
            ? ca.recommended_cip_start_date.findIndex((d) => d === date)
            : -1;

          // Predict Type (column 1)
          const predictTypeCell = row.getCell(startCol);
          if (
            predictedIdx >= 0 &&
            ca.predicted_cip_types &&
            ca.predicted_cip_types[predictedIdx]
          ) {
            predictTypeCell.value = ca.predicted_cip_types[predictedIdx];
          } else {
            predictTypeCell.value = "-";
          }
          Object.assign(predictTypeCell, DATA_STYLE);

          // Predict Reason (column 2)
          const predictReasonCell = row.getCell(startCol + 1);
          if (
            predictedIdx >= 0 &&
            ca.predicted_cip_reason &&
            ca.predicted_cip_reason[predictedIdx]
          ) {
            predictReasonCell.value = ca.predicted_cip_reason[predictedIdx];
          } else {
            predictReasonCell.value = "-";
          }
          Object.assign(predictReasonCell, DATA_STYLE);

          // Recommend Type (column 3)
          const recommendTypeCell = row.getCell(startCol + 2);
          if (
            recommendedIdx >= 0 &&
            ca.recommended_cip_type &&
            ca.recommended_cip_type[recommendedIdx]
          ) {
            recommendTypeCell.value = ca.recommended_cip_type[recommendedIdx];
          } else {
            recommendTypeCell.value = "-";
          }
          Object.assign(recommendTypeCell, DATA_STYLE);

          // Recommend Reason (column 4)
          const recommendReasonCell = row.getCell(startCol + 3);
          if (
            recommendedIdx >= 0 &&
            ca.recommend_cip_reason &&
            ca.recommend_cip_reason[recommendedIdx]
          ) {
            recommendReasonCell.value = ca.recommend_cip_reason[recommendedIdx];
          } else {
            recommendReasonCell.value = "-";
          }
          Object.assign(recommendReasonCell, DATA_STYLE);
          recommendReasonCell.alignment = { wrapText: true };
        }
      });
      row.commit();
    });

    // Set column widths
    sheet.getColumn(1).width = 15; // Start Date
    trainsData.forEach((_, trainIdx) => {
      const startCol = trainColMap[trainIdx];
      sheet.getColumn(startCol).width = 12; // Predict Type
      sheet.getColumn(startCol + 1).width = 20; // Reason
      sheet.getColumn(startCol + 2).width = 12; // Recommend Type
      sheet.getColumn(startCol + 3).width = 20; // Recommend Reason
    });

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

/**
 * Creates Benchmark CIP History Sheet (Pivot view)
 * Shows CIP/Shutdown by start date with plants as columns
 */
const createBenchmarkCIPHistorySheet = (sheet, plants) => {
  try {
    // Collect all unique dates and create plant identifier map
    const dateMap = new Map(); // date -> { "Plant X - Train Y": "CIP" or "Shutdown" }
    const plantsSet = new Set();

    plants.forEach((plant) => {
      if (plant && plant.trains && Array.isArray(plant.trains)) {
        plant.trains.forEach((train) => {
          const plantIdentifier = formatPlantTrainName(
            plant.plant_name || plant.name,
            plant.plant_id,
            train.train_num,
          );
          plantsSet.add(plantIdentifier);

          if (
            train &&
            train.historic_cips &&
            Array.isArray(train.historic_cips)
          ) {
            train.historic_cips.forEach((cip) => {
              const dateStr = cip.start_date
                ? formatDate(cip.start_date)
                : null;
              if (dateStr && dateStr !== "-") {
                if (!dateMap.has(dateStr)) {
                  dateMap.set(dateStr, {});
                }
                const dateEntry = dateMap.get(dateStr);
                const cipType = typeFormatter({ value: cip.type }, t) || "-";

                // If plant already has an entry for this date, append (comma-separated)
                if (dateEntry[plantIdentifier]) {
                  dateEntry[plantIdentifier] =
                    dateEntry[plantIdentifier] + ", " + cipType;
                } else {
                  dateEntry[plantIdentifier] = cipType;
                }
              }
            });
          }
        });
      }
    });

    const plantsList = Array.from(plantsSet).sort((a, b) => {
      // Extract plant_id and train_num from format: "plant_name(plant_id) - Train X"
      const aParts = a.match(/\((\d+)\).*Train (\d+)/);
      const bParts = b.match(/\((\d+)\).*Train (\d+)/);

      if (aParts && bParts) {
        const plantIdA = parseInt(aParts[1], 10);
        const plantIdB = parseInt(bParts[1], 10);

        // Sort by plant_id first, then by train_num
        if (plantIdA !== plantIdB) {
          return plantIdA - plantIdB;
        }
        return parseInt(aParts[2], 10) - parseInt(bParts[2], 10);
      }
      return a.localeCompare(b);
    });
    const sortedDates = Array.from(dateMap.keys()).sort(
      (a, b) => new Date(a) - new Date(b),
    );

    // Create header row
    const headerRow = sheet.getRow(1);
    headerRow.getCell(1).value = t("benchMark.headers.startDate");
    Object.assign(headerRow.getCell(1), HEADER_STYLE);

    plantsList.forEach((plant, idx) => {
      const cell = headerRow.getCell(idx + 2);
      cell.value = plant;
      Object.assign(cell, HEADER_STYLE);
    });
    headerRow.commit();

    // Add data rows
    sortedDates.forEach((date, dateIdx) => {
      const rowNum = 2 + dateIdx;
      const row = sheet.getRow(rowNum);

      row.getCell(1).value = date;
      Object.assign(row.getCell(1), DATA_STYLE);

      const dateEntry = dateMap.get(date);
      plantsList.forEach((plant, plantIdx) => {
        const cell = row.getCell(plantIdx + 2);
        cell.value = dateEntry[plant] || "-";
        Object.assign(cell, DATA_STYLE);
      });

      row.commit();
    });

    // Set column widths
    sheet.getColumn(1).width = 14; // Start Date
    plantsList.forEach((_, idx) => {
      sheet.getColumn(idx + 2).width = 22;
    });

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

/**
 * Creates Benchmark CIP Summary Sheet
 * Shows CIP/Shutdown summary by start date and plant (pivot-style)
 */
const createBenchmarkCIPSummarySheet = (sheet, plants) => {
  try {
    // Collect all historic CIP events from all plants
    const allCIPEvents = [];

    plants.forEach((plant) => {
      if (plant && plant.trains && Array.isArray(plant.trains)) {
        plant.trains.forEach((train) => {
          if (
            train &&
            train.historic_cips &&
            Array.isArray(train.historic_cips)
          ) {
            train.historic_cips.forEach((cip) => {
              allCIPEvents.push({
                ...cip,
                plant_id: plant.plant_id,
                plant_name: plant.plant_name,
                train_num: train.train_num,
                project_name: formatPlantTrainName(
                  plant.plant_name,
                  plant.plant_id,
                  train.train_num,
                ),
              });
            });
          }
        });
      }
    });

    // Sort by plant_id, then train_num, then start_date descending
    allCIPEvents.sort((a, b) => {
      // First sort by plant_id
      if (a.plant_id !== b.plant_id) {
        return a.plant_id - b.plant_id;
      }
      // Then sort by train_num
      if (a.train_num !== b.train_num) {
        return a.train_num - b.train_num;
      }
      // Finally sort by start_date descending
      return new Date(b.start_date) - new Date(a.start_date);
    });

    // Create header row with detailed columns
    const headerRow = sheet.getRow(1);
    const headers = [
      t("benchMark.headers.plantName"),
      t("benchMark.headers.startDate"),
      t("benchMark.headers.endDate"),
      t("benchMark.headers.type"),
      t("benchMark.headers.source"),
      t("benchMark.headers.acidCIPPH"),
      t("benchMark.headers.acidCIPTemp"),
      t("benchMark.headers.acidCIPDuration"),
      t("benchMark.headers.acidUsedPerCIP"),
      t("benchMark.headers.alkaliCIPPH"),
      t("benchMark.headers.alkaliCIPTemp"),
      t("benchMark.headers.alkaliCIPDuration"),
      t("benchMark.headers.alkaliUsedPerCIP"),
      t("benchMark.headers.cipSolutionPH"),
      t("benchMark.headers.cipSolutionTemp"),
      t("benchMark.headers.cipSolutionDuration"),
      t("benchMark.headers.additionalInformation"),
    ];

    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      Object.assign(cell, HEADER_STYLE);
    });
    headerRow.commit();

    // Add data rows
    allCIPEvents.forEach((cip, idx) => {
      const rowNum = 2 + idx;
      const row = sheet.getRow(rowNum);

      row.getCell(1).value = emptyValueFormatterForExcel(cip.project_name);
      row.getCell(2).value = emptyValueFormatterForExcel(cip.start_date);
      row.getCell(3).value = emptyValueFormatterForExcel(cip.end_date);
      row.getCell(4).value = typeFormatter({ value: cip.type }, t) || "-";
      row.getCell(5).value = capitalize(cip.source) || "-";
      row.getCell(6).value = emptyValueFormatterForExcel(cip.acid_cip_ph);
      row.getCell(7).value = emptyValueFormatterForExcel(cip.acid_cip_temp);
      row.getCell(8).value = emptyValueFormatterForExcel(cip.acid_cip_duration);
      row.getCell(9).value = emptyValueFormatterForExcel(cip.acid_used_per_cip);
      row.getCell(10).value = emptyValueFormatterForExcel(cip.alkali_cip_ph);
      row.getCell(11).value = emptyValueFormatterForExcel(cip.alkali_cip_temp);
      row.getCell(12).value = emptyValueFormatterForExcel(
        cip.alkali_cip_duration,
      );
      row.getCell(13).value = emptyValueFormatterForExcel(
        cip.alkali_used_per_cip,
      );
      row.getCell(14).value = emptyValueFormatterForExcel(cip.cip_solution_ph);
      row.getCell(15).value = emptyValueFormatterForExcel(
        cip.cip_solution_temp,
      );
      row.getCell(16).value = emptyValueFormatterForExcel(
        cip.cip_solution_duration,
      );
      row.getCell(17).value = emptyValueFormatterForExcel(
        cip.additional_information,
      );

      // Center alignment for all cells using DATA_STYLE
      for (let i = 1; i <= 17; i++) {
        Object.assign(row.getCell(i), DATA_STYLE);
      }

      row.commit();
    });

    // Set column widths (Train column removed)
    sheet.getColumn(1).width = 35; // Project Name
    sheet.getColumn(2).width = 12; // Start Date
    sheet.getColumn(3).width = 12; // End Date
    sheet.getColumn(4).width = 12; // Type
    sheet.getColumn(5).width = 12; // Source
    for (let i = 6; i <= 17; i++) {
      sheet.getColumn(i).width = 14;
    }

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

/**
 * Creates Benchmark Clean Effectiveness Sheet with cleaning efficiency metrics
 */
const createBenchmarkFoulingTypeSheet = (sheet, plants) => {
  try {
    // Collect all fouling type data from all plants
    const allFoulingData = [];

    plants.forEach((plant) => {
      // Check if this is a plant object with trains array or a flattened train object
      if (plant && plant.trains && Array.isArray(plant.trains)) {
        // Nested structure: plant -> trains
        plant.trains.forEach((train) => {
          const foulingData = parseJSON(train.fouling_data);

          // Extract fouling event data - API returns 'cip' not 'fouling_events'
          const cipData = foulingData.cip || {};
          const primaryReasons = foulingData.primary_reason || {};
          const secondaryReasons = foulingData.secondary_reason || {};

          // Create rows for each CIP event
          Object.keys(cipData).forEach((cipKey) => {
            const cip = cipData[cipKey];
            if (cip && cip.sd && cip.ed) {
              const primaryReason = primaryReasons[cipKey] || "-";
              const primaryProportion = foulingData.proportion1
                ? foulingData.proportion1[cipKey]
                : null;
              const secondaryReason = secondaryReasons[cipKey] || "-";
              const secondaryProportion = foulingData.proportion2
                ? foulingData.proportion2[cipKey]
                : null;

              allFoulingData.push({
                plant_id: plant.plant_id,
                plant_name: plant.plant_name || "Plant",
                train_num: train.train_num,
                type: typeFormatter({ value: cip.type }, t) || "CIP",
                start_date: cip.sd,
                end_date: cip.ed,
                primary_reason: primaryReason,
                primary_proportion: primaryProportion,
                secondary_reason: secondaryReason,
                secondary_proportion: secondaryProportion,
              });
            }
          });
        });
      } else if (plant && plant.fouling_data) {
        // Flattened structure: this is a train object with fouling_data
        const foulingData = parseJSON(plant.fouling_data);
        if (!foulingData || Object.keys(foulingData).length === 0) return;

        // Extract fouling event data - API returns 'cip' not 'fouling_events'
        const cipData = foulingData.cip || {};
        const primaryReasons = foulingData.primary_reason || {};
        const secondaryReasons = foulingData.secondary_reason || {};

        // Create rows for each CIP event
        Object.keys(cipData).forEach((cipKey) => {
          const cip = cipData[cipKey];
          if (cip && cip.sd && cip.ed) {
            const primaryReason = primaryReasons[cipKey] || "-";
            const primaryProportion = foulingData.proportion1
              ? foulingData.proportion1[cipKey]
              : null;
            const secondaryReason = secondaryReasons[cipKey] || "-";
            const secondaryProportion = foulingData.proportion2
              ? foulingData.proportion2[cipKey]
              : null;

            allFoulingData.push({
              plant_id: plant.plant_id,
              plant_name: plant.plant_name || "Plant",
              train_num: plant.train_num,
              type: typeFormatter({ value: cip.type }, t) || "CIP",
              start_date: cip.sd,
              end_date: cip.ed,
              primary_reason: primaryReason,
              primary_proportion: primaryProportion,
              secondary_reason: secondaryReason,
              secondary_proportion: secondaryProportion,
            });
          }
        });
      }
    });

    // Create header row
    const headerRow = sheet.getRow(1);
    const headers = [
      t("benchMark.headers.plantName"),
      t("benchMark.headers.type"),
      t("benchMark.headers.startDate"),
      t("benchMark.headers.endDate"),
      t("benchMark.headers.primaryReason"),
      t("benchMark.headers.secondaryReason"),
    ];

    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      Object.assign(cell, HEADER_STYLE);
    });
    headerRow.commit();

    // Add data rows
    allFoulingData.forEach((data, idx) => {
      const rowNum = 2 + idx;
      const row = sheet.getRow(rowNum);

      row.getCell(1).value = formatPlantTrainName(
        data.plant_name,
        data.plant_id,
        data.train_num,
      );
      row.getCell(2).value = data.type || "-";
      row.getCell(3).value = data.start_date
        ? formatDate(data.start_date)
        : "-";
      row.getCell(4).value = data.end_date ? formatDate(data.end_date) : "-";

      // Format reasons with proportions (same as UI file)
      const primaryReasonFormatted = formatReason(
        data.primary_reason,
        data.primary_proportion,
      );
      const secondaryReasonFormatted = formatReason(
        data.secondary_reason,
        data.secondary_proportion,
      );

      row.getCell(5).value = primaryReasonFormatted || "-";
      row.getCell(6).value = secondaryReasonFormatted || "-";

      // Center alignment and add light blue background for data rows
      for (let i = 1; i <= 6; i++) {
        const cell = row.getCell(i);
        Object.assign(cell, DATA_STYLE);
      }

      row.commit();
    });

    // Set column widths
    sheet.getColumn(1).width = 35; // Project
    sheet.getColumn(2).width = 12; // Type
    sheet.getColumn(3).width = 12; // Start Date
    sheet.getColumn(4).width = 12; // End Date
    sheet.getColumn(5).width = 25; // Primary Reason
    sheet.getColumn(6).width = 25; // Secondary Reason

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

const createReplacementAlertSheet = (sheet, plants) => {
  try {
    // Collect all replacement alert data from all plants
    const allReplacementData = [];

    plants.forEach((plant) => {
      // Check if this is a plant object with trains array or a flattened train object
      if (plant && plant.trains && Array.isArray(plant.trains)) {
        // Nested structure: plant -> trains
        plant.trains.forEach((train) => {
          const raPredictions = parseJSON(train.ra_predictions);

          // Extract replacement results
          const replacementResults = raPredictions.replacement_results || [];

          // Create rows for each replacement alert
          if (replacementResults.length > 0) {
            replacementResults.forEach((alert) => {
              allReplacementData.push({
                plant_id: plant.plant_id,
                plant_name: plant.plant_name || "Plant",
                train_num: train.train_num,
                forecasted_date: alert.forecasted_date || "-",
                trigger: alert.trigger || "-",
                explanation: alert.explanation || "-",
              });
            });
          } else {
            // Add a row with "-" if no replacement results
            allReplacementData.push({
              plant_id: plant.plant_id,
              plant_name: plant.plant_name || "Plant",
              train_num: train.train_num,
              forecasted_date: "-",
              trigger: "-",
              explanation: "-",
            });
          }
        });
      } else if (plant && plant.ra_predictions) {
        // Flattened structure: this is a train object with ra_predictions
        const raPredictions = parseJSON(plant.ra_predictions);
        const replacementResults = raPredictions.replacement_results || [];

        // Create rows for each replacement alert
        if (replacementResults.length > 0) {
          replacementResults.forEach((alert) => {
            allReplacementData.push({
              plant_id: plant.plant_id,
              plant_name: plant.plant_name || "Plant",
              train_num: plant.train_num,
              forecasted_date: alert.forecasted_date || "-",
              trigger: alert.trigger || "-",
              explanation: alert.explanation || "-",
            });
          });
        } else {
          // Add a row with "-" if no replacement results
          allReplacementData.push({
            plant_id: plant.plant_id,
            plant_name: plant.plant_name || "Plant",
            train_num: plant.train_num,
            forecasted_date: "-",
            trigger: "-",
            explanation: "-",
          });
        }
      }
    });

    // Create header row
    const headerRow = sheet.getRow(1);
    const headers = [
      t("benchMark.headers.plantName"),
      t("benchMark.headers.forecastedDate"),
      t("benchMark.headers.trigger"),
      t("benchMark.headers.potentialExplanation"),
    ];

    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      Object.assign(cell, HEADER_STYLE);
    });
    headerRow.commit();

    // Add data rows
    allReplacementData.forEach((data, idx) => {
      const rowNum = 2 + idx;
      const row = sheet.getRow(rowNum);

      row.getCell(1).value = formatPlantTrainName(
        data.plant_name,
        data.plant_id,
        data.train_num,
      );
      row.getCell(2).value =
        data.forecasted_date !== "-" ? formatDate(data.forecasted_date) : "-";
      row.getCell(3).value = data.trigger || "-";
      row.getCell(4).value = data.explanation || "-";

      // Light blue background for data rows
      for (let i = 1; i <= 4; i++) {
        Object.assign(row.getCell(i), DATA_STYLE);
      }

      row.commit();
    });

    // Auto-fit column widths
    sheet.columns = [
      { width: 35 }, // Project
      { width: 18 }, // Forecasted Date
      { width: 30 }, // Trigger
      { width: 30 }, // Explanation
    ];

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

const createBenchmarkCleanEffectivenessSheet = (sheet, plants) => {
  try {
    // Collect all cleaning effectiveness data from all plants
    const allCleaningData = [];

    plants.forEach((plant) => {
      if (plant && plant.trains && Array.isArray(plant.trains)) {
        plant.trains.forEach((train) => {
          const cleaningEfficiency = parseJSON(train.cleaning_efficiency);
          if (
            !cleaningEfficiency ||
            Object.keys(cleaningEfficiency).length === 0
          )
            return;

          // Extract CIP data and metrics
          const cipData = cleaningEfficiency.cip || {};
          const normPressureDrop = cleaningEfficiency.norm_pressure_drop || {};
          const normPermeateFlow = cleaningEfficiency.norm_permeate_flow || {};
          const normSaltPassage = cleaningEfficiency.norm_salt_passage || {};
          const normWaterPermeability =
            cleaningEfficiency.norm_water_permeability || {};
          const normPressureDropFlag =
            cleaningEfficiency.norm_pressure_drop_flag || {};
          const normPermeateFlowFlag =
            cleaningEfficiency.norm_permeate_flow_flag || {};
          const normSaltPassageFlag =
            cleaningEfficiency.norm_salt_passage_flag || {};
          const cipReasons = cleaningEfficiency.cip_reason || {};

          // Create rows for each CIP event
          Object.keys(cipData).forEach((cipKey) => {
            const cip = cipData[cipKey];
            if (cip) {
              const cipReason = cipReasons[cipKey];
              let reasonText = "-";

              if (Array.isArray(cipReason)) {
                reasonText = cipReason.filter((r) => r).join(", ");
              } else if (cipReason) {
                reasonText = cipReason;
              }

              allCleaningData.push({
                plant_id: plant.plant_id,
                plant_name: plant.plant_name,
                train_num: train.train_num,
                index: parseInt(cipKey) + 1, // 1-based index for display
                type:
                  cip.type === "C"
                    ? typeFormatter({ value: "C" }, t)
                    : cip.type === "S"
                      ? typeFormatter({ value: "S" }, t)
                      : cip.type,
                start_date: cip.sd,
                cip_reason: reasonText,
                norm_pressure_drop:
                  normPressureDrop[cipKey] !== null &&
                  normPressureDrop[cipKey] !== undefined
                    ? normPressureDrop[cipKey]
                    : null,
                norm_permeate_flow:
                  normPermeateFlow[cipKey] !== null &&
                  normPermeateFlow[cipKey] !== undefined
                    ? normPermeateFlow[cipKey]
                    : null,
                norm_salt_passage:
                  normSaltPassage[cipKey] !== null &&
                  normSaltPassage[cipKey] !== undefined
                    ? normSaltPassage[cipKey]
                    : null,
                norm_water_permeability:
                  normWaterPermeability[cipKey] !== null &&
                  normWaterPermeability[cipKey] !== undefined
                    ? normWaterPermeability[cipKey]
                    : null,
                norm_pressure_drop_flag: normPressureDropFlag?.[cipKey] || [],
                norm_permeate_flow_flag: normPermeateFlowFlag?.[cipKey] || [],
                norm_salt_passage_flag: normSaltPassageFlag?.[cipKey] || [],
              });
            }
          });
        });
      }
    });

    // Create header row
    const headerRow = sheet.getRow(1);
    const headers = [
      t("benchMark.headers.plantName"),
      t("benchMark.headers.type"),
      t("benchMark.headers.startDate"),
      t("benchMark.headers.cipReason"),
      t("benchMark.headers.pressure"),
      t("benchMark.headers.flow"),
      t("benchMark.headers.saltPassage"),
      t("benchMark.headers.normalizedWaterPermeability"),
    ];

    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      Object.assign(cell, HEADER_STYLE);
    });
    headerRow.commit();

    // Add data rows
    allCleaningData.forEach((data, idx) => {
      const rowNum = 2 + idx;
      const row = sheet.getRow(rowNum);

      row.getCell(1).value = formatPlantTrainName(
        data.plant_name,
        data.plant_id,
        data.train_num,
      );
      row.getCell(2).value = data.type || "-";
      // Format date as DD/MM/YYYY
      row.getCell(3).value = data.start_date
        ? formatDate(data.start_date)
        : "-";
      row.getCell(4).value = data.cip_reason || "-";

      // Pressure: add flag superscripts if set
      const pressureFlags = Array.isArray(data.norm_pressure_drop_flag)
        ? data.norm_pressure_drop_flag.join("")
        : "";
      const pressureValue = formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_pressure_drop,
          pressureFlags || null,
        ),
      );
      row.getCell(5).value = pressureValue;

      // Flow: add flag superscripts if set
      const flowFlags = Array.isArray(data.norm_permeate_flow_flag)
        ? data.norm_permeate_flow_flag.join("")
        : "";
      const flowValue = formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_permeate_flow,
          flowFlags || null,
        ),
      );
      row.getCell(6).value = flowValue;

      // Salt Passage: add flag superscripts if set
      const saltFlags = Array.isArray(data.norm_salt_passage_flag)
        ? data.norm_salt_passage_flag.join("")
        : "";
      const saltValue = formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_salt_passage,
          saltFlags || null,
        ),
      );
      row.getCell(7).value = saltValue;

      // Normalized Water Permeability: format with validation
      const waterPermValue = formatNumericValue(data.norm_water_permeability);
      row.getCell(8).value = waterPermValue;

      // Center alignment and add light blue background for data rows
      for (let i = 1; i <= 8; i++) {
        const cell = row.getCell(i);
        Object.assign(cell, DATA_STYLE);
      }

      row.commit();
    });

    // Set column widths
    sheet.getColumn(1).width = 35; // Project Name (plant_name(id) - Train X)
    sheet.getColumn(2).width = 12; // Type
    sheet.getColumn(3).width = 12; // Start Date
    sheet.getColumn(4).width = 30; // CIP Reason
    for (let i = 5; i <= 8; i++) {
      sheet.getColumn(i).width = 14;
    }
    sheet.getColumn(6).width = 12; // Pressure
    sheet.getColumn(7).width = 12; // Flow
    sheet.getColumn(8).width = 25; // Normalized Water Permeability

    // Add borders
    addBordersToSheet(sheet);
  } catch (error) {
    throw error;
  }
};

/**
 * Main export function for CA Predictions Report - 4 Sheets
 * @param {Array} trainsData - Array of trains with ca_predictions data (flattened, for sheets 1-2)
 * @param {string} filename - Output filename (optional)
 * @param {Array} plantsData - Array of plants with full structure including historic_cips (for sheet 3)
 * @param {number} filterType - Filter type (1, 2, or 3)
 * @param {Object} units - Units object from API response with currency and volume units
 */
const exportExcelReport = async (
  trainsData,
  filename = null,
  plantsData = null,
  filterType = 3,
  units = null,
) => {
  try {
    if (!Array.isArray(trainsData)) {
      throw new Error("trainsData must be an array");
    }

    // Map optimization_target API values to display names
    const optimizationTargetMap = {
      total_cost: "Maximize Financial Benefits",
      maximize_product: "Maximize Product",
      minimize_energy: "Minimize Energy Cost",
    };

    // Get currency symbol from units
    const getCurrencySymbol = (currencyCode) => {
      const symbols = {
        USD: "$",
        Euro: "€",
        Yuan: "¥",
      };
      return symbols[currencyCode] || currencyCode;
    };

    const currencySymbol =
      units && units.currency ? getCurrencySymbol(units.currency) : "$";
    const volumeUnit = units && units.volume ? units.volume : "m³";

    const parsedTrainsData = trainsData
      .map((train) => {
        if (!train || typeof train !== "object") {
          return null;
        }

        const trainObj = {
          train_num: train.train_num || 0,
          plant_id: train.plant_id || null,
          plant_name: train.plant_name || null,
          train_name: train.train_name || `Train ${train.train_num || 0}`,
          historic_cips: train.historic_cips || [],
          optimization_target:
            optimizationTargetMap[train.optimization_target] ||
            "Maximize Financial Benefits",
        };

        if (typeof train.ca_predictions === "string") {
          try {
            trainObj.ca_predictions = JSON.parse(train.ca_predictions);
          } catch (parseError) {
            trainObj.ca_predictions = {};
          }
        } else {
          trainObj.ca_predictions = train.ca_predictions || {};
        }

        trainObj.cleaning_efficiency = train.cleaning_efficiency || null;
        trainObj.fouling_data = train.fouling_data || null;
        trainObj.ra_predictions = train.ra_predictions || null;

        return trainObj;
      })
      .filter((train) => train !== null);

    if (parsedTrainsData.length === 0) {
      throw new Error("No valid trains data to export");
    }

    const workbook = new ExcelJS.Workbook();

    try {
      // filterType: 1 = hide sheet 7, 2 = hide sheets 1-6, 3 = include all
      const includeSheets1To6 = filterType !== 2;
      const includeSheet7 = filterType !== 1;

      // Sheet 1: Benchmark Optimization Results
      if (includeSheets1To6) {
        const sheet1 = workbook.addWorksheet(
          t("benchMark.sheetNames.optimizationResults"),
        );
        createBenchmarkOptimizationSheet(
          sheet1,
          parsedTrainsData,
          currencySymbol,
          volumeUnit,
        );
      }

      // Sheet 2: Benchmark CIP Predict Recommend
      if (includeSheets1To6) {
        const sheet2 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipPredictRecommend"),
        );
        createBenchmarkCIPPredictRecommendSheet(sheet2, parsedTrainsData);
      }

      // Sheet 3: Benchmark CIP History
      if (includeSheets1To6) {
        const sheet3 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipHistory"),
        );
        if (plantsData && Array.isArray(plantsData) && plantsData.length > 0) {
          createBenchmarkCIPHistorySheet(sheet3, plantsData);
        } else {
          createBenchmarkCIPHistorySheet(sheet3, parsedTrainsData);
        }
      }

      // Sheet 4: Benchmark CIP Details
      if (includeSheets1To6) {
        const sheet4 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipDetails"),
        );
        if (plantsData && Array.isArray(plantsData) && plantsData.length > 0) {
          createBenchmarkCIPSummarySheet(sheet4, plantsData);
        } else {
          createBenchmarkCIPSummarySheet(sheet4, parsedTrainsData);
        }
      }

      // Sheet 5: Benchmark Clean Effectiveness
      if (includeSheets1To6) {
        const sheet5 = workbook.addWorksheet(
          t("benchMark.sheetNames.cleanEffectiveness"),
        );
        if (plantsData && Array.isArray(plantsData) && plantsData.length > 0) {
          createBenchmarkCleanEffectivenessSheet(sheet5, plantsData);
        } else {
          createBenchmarkCleanEffectivenessSheet(sheet5, parsedTrainsData);
        }
      }

      // Sheet 6: Benchmark Fouling Type
      if (includeSheets1To6) {
        const sheet6 = workbook.addWorksheet(
          t("benchMark.sheetNames.foulingType"),
        );
        if (plantsData && Array.isArray(plantsData) && plantsData.length > 0) {
          createBenchmarkFoulingTypeSheet(sheet6, plantsData);
        } else {
          createBenchmarkFoulingTypeSheet(sheet6, parsedTrainsData);
        }
      }

      // Sheet 7: Benchmark Lifetime Estimates
      if (includeSheet7) {
        const sheet7 = workbook.addWorksheet(
          t("benchMark.sheetNames.lifetimeEstimates"),
        );
        if (plantsData && Array.isArray(plantsData) && plantsData.length > 0) {
          createReplacementAlertSheet(sheet7, plantsData);
        } else {
          createReplacementAlertSheet(sheet7, parsedTrainsData);
        }
      }
    } catch (sheetError) {
      throw new Error(`Failed to create sheets: ${sheetError.message}`);
    }

    // Generate filename with timestamp if not provided
    const exportFilename = filename || t("benchMark.benchmarkReportName");

    // Save and download
    await saveExcelFile(exportFilename, workbook);

    return { success: true, filename: exportFilename };
  } catch (error) {
    throw new Error(`Failed to export Excel Report: ${error.message}`);
  }
};

export default exportExcelReport;
